import React from 'react';

/**
 * Turns **bold**, _italic_ and ^^soft grey^^ markers (written via the admin toolbar,
 * not raw HTML) into real <strong>/<em> elements — built as React nodes,
 * never dangerouslySetInnerHTML, so there is no XSS surface even though
 * the source is admin-authored text.
 */
const MENTION_PART = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/;
// http(s) only, by construction — never javascript: or other schemes.
const URL_PART = /(https?:\/\/[^\s<>"')\]]+)/;

const parseInline = (text) => {
  const parts = text.split(/(\^\^[^^]+\^\^|\*\*[^*]+\*\*|_[^_]+_|@\[[^\]]+\]\([0-9a-f-]{36}\)|https?:\/\/[^\s<>"')\]]+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('^^') && part.endsWith('^^') && part.length > 4) return <span key={i} className="text-gray-500">{parseInline(part.slice(2, -2))}</span>;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('_') && part.endsWith('_')) return <em key={i}>{part.slice(1, -1)}</em>;
    const mentionMatch = part.match(MENTION_PART);
    if (mentionMatch) {
      return <span key={i} className="font-medium text-reset-terracotta bg-reset-terracotta/10 rounded px-1">@{mentionMatch[1]}</span>;
    }
    if (URL_PART.test(part)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-reset-terracotta underline break-all">{part}</a>;
    }
    return part;
  });
};

/**
 * Splits plain text into blocks on blank lines. A block where every line
 * starts with -, •, or * renders as a real bullet list instead of flat
 * text; everything else renders as its own paragraph. Also strips stray
 * non-breaking spaces that sneak in from pasted Google Docs text. Shared
 * by anywhere admin-authored long-form content (lesson transcripts,
 * sequence emails, Hub announcements) needs to render "laid out," not as
 * a raw text blob.
 */
export const renderRichText = (text) => {
  if (!text) return null;
  const clean = text.replace(/\u00A0/g, ' ');
  const blocks = clean.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
    const isBulletList = lines.length > 1 && lines.every(l => /^[-•*]\s+/.test(l));
    if (isBulletList) {
      return (
        <ul key={i} className="list-disc pl-5 space-y-1 mb-3 last:mb-0">
          {lines.map((l, j) => <li key={j}>{parseInline(l.replace(/^[-•*]\s+/, ''))}</li>)}
        </ul>
      );
    }
    return <p key={i} className="mb-3 last:mb-0 whitespace-pre-line">{parseInline(block)}</p>;
  });
};
